import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Chip, Typography } from '@mui/material';
import { StyledCheckbox } from './MUIStyledComponents';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';

const PREFIX = 'CustomAutocomplete';

const classes = {
  formControl: `${PREFIX}-formControl`,
  chips: `${PREFIX}-chips`,
  chip: `${PREFIX}-chip`
};

const StyledFormControl = styled(FormControl)(() =>
  ({
    [`&.${classes.formControl}`]: {
      minHeight: '44px',
      width: '100%',
      borderRadius: '8px',
      border: '1px solid #4A4C51;',
      background: '#2E3036',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      height: '100%'
    },

    [`& .${classes.chips}`]: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginLeft: 6,
      padding: '3px',
    },

    [`& .${classes.chip}`]: {
      borderRadius: '6px',
      background: '#1A1B1E',
      color: '#F5FAFF',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '20px',
      height: '24px',
      padding: '12px 0px',
      overflow: 'hidden',
      whiteSpace: 'nowrap'
    }
  }));

interface IMultiProps {
  options: any[];
  placeholder: string;
  onChange: any;
  toClear: boolean;
}

const CustomAutocomplete: React.FC<IMultiProps> = ({ options, onChange, toClear, placeholder }) => {
  const [ selected, setSelected ] = React.useState<any | null>([]);

  const handleChange = (event: any, newValue: any | null, reason: any) => {
    if (reason === 'selectOption') {
      let valueToAdd = newValue.map((option: any) => option.key || option);
      valueToAdd = valueToAdd[valueToAdd.length - 1];
      setSelected([ ...selected, valueToAdd ]);
    }
    if (reason === 'removeOption') {
      setSelected(newValue);
    }
  };

  useEffect(() => {
    if (toClear) {
      setSelected([]);
    }
  }, [ toClear ]);

  useEffect(() => {
    onChange(selected);
  }, [ selected ]);

  return (
    <StyledFormControl className={classes.formControl}>
      <Autocomplete
        value={selected}
        multiple
        onChange={(e: any, newValue: any | null, reason: any) => handleChange(e, newValue, reason)}
        options={options}
        noOptionsText={'No options available'}
        getOptionLabel={(option) => option.key}
        isOptionEqualToValue={(option, value) => option.key === value}
        renderOption={(props, opt) => (
          <>
            {opt && opt.key && (
              <MenuItem style={{ whiteSpace: 'pre-wrap' }} value={opt.key} {...props}>
                <StyledCheckbox checked={selected.indexOf(opt.key) > -1} />
                <ListItemText primary={opt.key} />
              </MenuItem>
            )}
          </>
        )}
        renderTags={() => {
          if (selected.length === 0) {
            return <Typography className='custom-select-text'>{placeholder}</Typography>;
          }
          return <div className={classes.chips}>
            {(selected as string[]).map((option: string, index: number) => (
              <Chip key={index} className={classes.chip} label={option} />
            ))}
          </div>;
        }
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length === 0 ? placeholder : ''}
          />
        )
        }
      />
    </StyledFormControl>
  );
};

export default CustomAutocomplete;
